<template>
  <div class="address-form">
    <table class="first-dark">
      <tbody>
        <tr>
          <td>{{ $t("message.order.name") }}</td>
          <td>
            <input
              class="regular full bordered"
              v-if="canEdit"
              type="text"
              name="name"
              placeholder="name"
              v-model="localAddress.name"
              :disabled="disabled"
            />
            <template v-else>
              {{ localAddress.name }}
            </template>
          </td>
        </tr>
        <tr>
          <td>{{ $t("message.order.company") }}</td>
          <td>
            <input
              class="regular full bordered"
              v-if="canEdit"
              type="text"
              name="company"
              placeholder="company"
              v-model="localAddress.company"
              :disabled="disabled"
            />
            <template v-else>
              {{ localAddress.company }}
            </template>
          </td>
        </tr>
        <tr>
          <td>{{ $t("message.order.street_1") }}</td>
          <td>
            <GoogleAddressSearch
              v-if="canEdit && mounted && !disabled"
              v-model="localAddress.street1"
              placeholder="Start typing your address..."
              @address-selected="handleGoogleAddressSelected"
            />
            <input
              v-else-if="canEdit && disabled"
              class="regular full bordered"
              type="text"
              name="street1"
              placeholder="street1"
              v-model="localAddress.street1"
              disabled
            />
            <template v-else>
              {{ localAddress.street1 }}
            </template>
          </td>
        </tr>
        <tr>
          <td>{{ $t("message.order.street_2") }}</td>
          <td>
            <input
              class="regular full bordered"
              v-if="canEdit"
              type="text"
              name="street2"
              placeholder="street2"
              v-model="localAddress.street2"
              :disabled="disabled"
            />
            <template v-else>
              {{ localAddress.street2 }}
            </template>
          </td>
        </tr>
        <tr>
          <td>{{ $t("message.order.street_3") }}</td>
          <td>
            <input
              class="regular full bordered"
              v-if="canEdit"
              type="text"
              name="street3"
              placeholder="street3"
              v-model="localAddress.street3"
              :disabled="disabled"
            />
            <template v-else>
              {{ localAddress.street3 }}
            </template>
          </td>
        </tr>
        <tr>
          <td>{{ $t("message.order.city") }}</td>
          <td>
            <input
              class="regular full bordered"
              v-if="canEdit"
              type="text"
              name="city"
              placeholder="city"
              v-model="localAddress.city"
              :disabled="disabled"
            />
            <template v-else>
              {{ localAddress.city }}
            </template>
          </td>
        </tr>
        <tr>
          <td>{{ $t("message.order.state") }}</td>
          <td>
            <template v-if="canEdit">
              <input
                v-if="localAddress.country != 'US'"
                class="regular full bordered"
                type="text"
                name="state"
                v-model="localAddress.state"
                :disabled="disabled"
              />
              <select
                class="regular full bordered"
                v-else
                name="state"
                v-model="localAddress.state"
                :disabled="disabled"
              >
                <option value="">{{ $t("message.order.select_state") }}</option>
                <option
                  v-for="state in states"
                  :key="state.shortCode"
                  :value="state.shortCode"
                >
                  {{ state.label }}
                </option>
              </select>
            </template>
            <template v-else>
              {{ localAddress.state }}
            </template>
          </td>
        </tr>
        <tr>
          <td>{{ $t("message.order.country") }}</td>
          <td>
            <select
              class="regular full bordered"
              v-if="canEdit"
              name="country"
              v-model="localAddress.country"
              :disabled="disabled"
            >
              <option
                v-for="country in countriesWithSelected"
                :key="country.code"
                :value="country.code"
              >
                {{ country.label }}
              </option>
            </select>
            <template v-else>
              {{ localAddress.country }}
            </template>
          </td>
        </tr>
        <tr>
          <td>{{ $t("message.order.postal_code") }}</td>
          <td>
            <input
              class="regular full bordered"
              v-if="canEdit"
              type="text"
              name="postalCode"
              placeholder="postal code"
              v-model="localAddress.postalCode"
              :disabled="disabled"
            />
            <template v-else>
              {{ localAddress.postalCode }}
            </template>
          </td>
        </tr>
        <tr>
          <td>{{ $t("message.order.residential") }}</td>
          <td>
            <!-- No se elige: ShipStation lo detecta con la calle al guardar -->
            <ResidentialBadge
              :residential="localAddress.residential"
              :verified="localAddress.addressVerified"
            />
          </td>
        </tr>
        <tr>
          <td>{{ $t("message.order.phone") }}</td>
          <td>
            <div v-if="canEdit" class="flex flex-row gap-2">
              <input
                class="regular bordered w-24"
                type="text"
                name="phone-area-code"
                :placeholder="$t('create_infoauto_customer_modal.area_code')"
                :maxlength="areaCodeMaxLength"
                v-model.trim="localAddress.phoneAreaCode"
                :disabled="phoneDisabled"
              />
              <input
                class="regular full bordered"
                type="text"
                name="phone"
                placeholder="phone"
                v-model="localAddress.phone"
                :disabled="phoneDisabled"
              />
            </div>
            <template v-else>
              {{ formatPhone(localAddress.phoneAreaCode, localAddress.phone) }}
            </template>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import GoogleAddressSearch from "@/components/global/GoogleAddressSearch.vue";
import ResidentialBadge from "@/components/shared/ResidentialBadge.vue";
import { getInfoautoCountries } from "@/api/store/Infoauto.js";
import { AREA_CODE_MAX_LENGTH } from "@/constants/infoautoPersona";
import { formatPhone } from "@/utils/phone";

const states = require("@/constants/states.json");

export default {
  name: "StandardAddressForm",
  components: {
    GoogleAddressSearch,
    ResidentialBadge
  },
  props: {
    address: {
      type: Object,
      required: true
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    phoneDisabled: {
      type: Boolean,
      default: false
    },
    mounted: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localAddress: JSON.parse(JSON.stringify(this.address)),
      countries: [],
      areaCodeMaxLength: AREA_CODE_MAX_LENGTH
    };
  },
  computed: {
    states() {
      const keys = Object.keys(states);
      return keys.map(key => {
        return {
          shortCode: key,
          label: states[key]
        };
      });
    },
    countriesWithSelected() {
      // Direcciones viejas o de Google pueden traer un código que no está en
      // infoauto_paises: se agrega para no perderlo al mostrar el select
      const selectedCountry = this.localAddress.country;
      const countryExists = this.countries.some(
        country => country.code === selectedCountry
      );
      if (!countryExists && selectedCountry) {
        return [
          ...this.countries,
          { code: selectedCountry, label: selectedCountry }
        ];
      }
      return this.countries;
    }
  },
  created() {
    this.loadCountries();
  },
  watch: {
    address: {
      handler(val) {
        // Only update localAddress if external prop changed (not from our emit)
        // Use deep comparison to avoid unnecessary updates
        if (JSON.stringify(val) !== JSON.stringify(this.localAddress)) {
          this.localAddress = JSON.parse(JSON.stringify(val));
        }
      },
      deep: true
    },
    localAddress: {
      handler(val) {
        this.$emit("update:address", val);
      },
      deep: true
    }
  },
  methods: {
    formatPhone,
    async loadCountries() {
      try {
        this.countries = await getInfoautoCountries();
      } catch (error) {
        console.error("Error loading countries:", error);
        this.$toast.error("Failed to load countries");
      }
    },
    handleGoogleAddressSelected(addressData) {
      if (addressData.street1) {
        this.localAddress.street1 = addressData.street1;
      }
      if (addressData.city) {
        this.localAddress.city = addressData.city;
      }
      if (addressData.state) {
        this.localAddress.state = addressData.state;
      }
      if (addressData.postal_code) {
        this.localAddress.postalCode = addressData.postal_code;
      }
      if (addressData.country) {
        this.localAddress.country = addressData.country;
      }
      this.$emit("google-address-selected", addressData);
    }
  }
};
</script>

<style scoped>
.first-dark td:first-child {
  background-color: #222222;
  color: white;
  font-weight: 600;
  padding-left: 8px;
  width: 120px;
}

.first-dark tr:nth-child(even) {
  background-color: #f9fafb;
}
</style>
